import React, { useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import { useGlobalContext } from "context/globalContext";
import { IoMdClose } from "react-icons/io";
import {
  createOrUpdateOnboardTemplateAPI,
  updateOnboardTemplateAPI,
} from "Api/onBoardAPI";
import { getConfigAPI } from "Api/config";

const CreateOrUpdateOnboard = ({
  isOpen,
  onClose,
  onSubmit,
  isEditMode,
  isAddMode,
}) => {
  const {
    messageFormData,
    setMessageFormData,
    initialMessageFormState,
    setFetchMessage,
  } = useGlobalContext();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [charLimits, setCharLimits] = useState(null);
  const [isLoadingLimits, setIsLoadingLimits] = useState(false);

  useEffect(() => {
    const fetchCharLimits = async () => {
      if (!isOpen || charLimits) return;

      setIsLoadingLimits(true);
      try {
        const token = localStorage.getItem("jwtToken");

        const configData = await getConfigAPI(
          { pageIndex: 0, skip: 50 },
          null,
          token
        );

        if (configData && configData.data && Array.isArray(configData.data)) {
          const charLimitConfig = configData.data.find(
            (item) => item.key === "onBoarding_char_limit"
          );

          if (charLimitConfig?.value) {
            let parsedLimits;
            if (typeof charLimitConfig.value === "string") {
              try {
                const fixedJson = charLimitConfig.value
                  .replace(
                    /([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_$]*)(\s*:)/g,
                    '$1"$2"$3'
                  )
                  .replace(/\s+/g, " ")
                  .replace(/'/g, '"');

                parsedLimits = JSON.parse(fixedJson);
              } catch (error) {
                console.error("Error parsing char limits:", error);
                parsedLimits = { min: 10, max: 15 };
              }
            } else {
              parsedLimits = charLimitConfig.value;
            }

            setCharLimits({
              min: parsedLimits.min || 10,
              max: parsedLimits.max || 15,
            });
          } else {
            setCharLimits({ min: 10, max: 15 });
          }
        } else {
          setCharLimits({ min: 10, max: 15 });
        }
      } catch (error) {
        console.error("Error fetching char limits:", error);
        setCharLimits({ min: 10, max: 15 });
      } finally {
        setIsLoadingLimits(false);
      }
    };

    fetchCharLimits();
  }, [isOpen, charLimits]);

  useEffect(() => {
    if (!isOpen) {
      setCharLimits(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.height = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.height = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.height = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!isEditMode && !isAddMode) {
      setMessageFormData((prev) => ({
        ...prev,
        status: prev.status || "active",
      }));
    }
    setMessageFormData((prev) => {
      const signUpData = prev.signUp || {};
      const welcomeData = prev.welcome || {};

      return {
        ...prev,
        templateType: prev.templateType || "signUp",

        title:
          prev.templateType === "signUp"
            ? signUpData.title || ""
            : prev.title || "",
        subTitle:
          prev.templateType === "signUp"
            ? signUpData.subTitle || ""
            : prev.subTitle || "",
        specs:
          prev.templateType === "signUp"
            ? signUpData.specs || []
            : prev.specs || [],
        showLoginButton:
          prev.templateType === "signUp"
            ? signUpData.showLoginButton ?? false
            : prev.showLoginButton ?? false,
        showDownArrow:
          prev.templateType === "signUp"
            ? signUpData.showDownArrow ?? false
            : prev.showDownArrow ?? false,
        paramValue:
          prev.templateType === "signUp"
            ? signUpData.paramValue || ""
            : prev.paramValue || "",

        booking:
          prev.templateType === "signUp"
            ? signUpData.booking || "flight"
            : prev.booking || "flight",

        welcome: {
          title: welcomeData.title || "",
          subTitle: welcomeData.subTitle || "",
          buttonName: welcomeData.buttonName || "",
          loginLink: welcomeData.loginLink || "",
        },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, setMessageFormData]);

  const validateCharLimit = (value, fieldName) => {
    if (!value || !charLimits) return true;

    const length = value.length;
    if (length < charLimits.min) {
      toast.error(`${fieldName} must be at least ${charLimits.min} characters`);
      return false;
    }
    if (length > charLimits.max) {
      toast.error(`${fieldName} must be at most ${charLimits.max} characters`);
      return false;
    }
    return true;
  };

  const handleChange = (e) => {
    const { name, type, value, files } = e.target;

    if (type === "text" || type === "textarea") {
      setMessageFormData({ ...messageFormData, [name]: value });
    } else if (type === "file") {
      setMessageFormData({ ...messageFormData, [name]: files[0] });
    } else if (name === "showLoginButton" || name === "showDownArrow") {
      setMessageFormData({ ...messageFormData, [name]: value === "true" });
    } else {
      setMessageFormData({ ...messageFormData, [name]: value });
    }
  };

  const handleWelcomeChange = (field, value) => {
    setMessageFormData({
      ...messageFormData,
      welcome: {
        ...messageFormData.welcome,
        [field]: value,
      },
    });
  };

  const handleArrayChange = (field, index, key, value) => {
    const updatedArray = [...(messageFormData[field] || [])];
    updatedArray[index][key] = value;
    setMessageFormData({ ...messageFormData, [field]: updatedArray });
  };

  const addSpec = () => {
    setMessageFormData({
      ...messageFormData,
      specs: [
        ...(messageFormData.specs || []),
        { icon: "check_small", label: "" },
      ],
    });
  };

  const removeSpec = (index) => {
    const updatedSpecs = [...(messageFormData.specs || [])];
    updatedSpecs.splice(index, 1);
    setMessageFormData({ ...messageFormData, specs: updatedSpecs });
  };

  const CharCountDisplay = ({ value, fieldName }) => {
    if (!value || !charLimits) return null;

    const length = value.length;
    const isValid = length >= charLimits.min && length <= charLimits.max;

    return (
      <div
        className={`mt-1 text-xs ${
          isValid ? "text-green-600" : "text-red-600"
        }`}
      >
        {length} / {charLimits.max} characters
        {length < charLimits.min && ` (Minimum ${charLimits.min} required)`}
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const fieldsToValidate = [];

    if (messageFormData.templateType === "signUp") {
      if (messageFormData.title) {
        fieldsToValidate.push({ value: messageFormData.title, name: "Title" });
      }
      if (messageFormData.subTitle) {
        fieldsToValidate.push({
          value: messageFormData.subTitle,
          name: "Subtitle",
        });
      }
    } else if (messageFormData.templateType === "welcome") {
      const welcome = messageFormData.welcome || {};
      if (welcome.title) {
        fieldsToValidate.push({ value: welcome.title, name: "Welcome Title" });
      }
      if (welcome.subTitle) {
        fieldsToValidate.push({
          value: welcome.subTitle,
          name: "Welcome Subtitle",
        });
      }
    }

    if (messageFormData.paramValue) {
      fieldsToValidate.push({ value: messageFormData.paramValue, name: "TID" });
    }

    if (messageFormData.specs) {
      messageFormData.specs.forEach((spec, index) => {
        if (spec.label) {
          fieldsToValidate.push({
            value: spec.label,
            name: `Spec ${index + 1}`,
          });
        }
      });
    }

    for (const field of fieldsToValidate) {
      if (!validateCharLimit(field.value, field.name)) {
        return;
      }
    }

    setIsSubmitting(true);
    const token = localStorage.getItem("jwtToken");

    const payload = {
      ...messageFormData,
      type: messageFormData.templateType,
      signUp:
        messageFormData.templateType === "signUp"
          ? {
              title: messageFormData.title,
              subTitle: messageFormData.subTitle,
              specs: messageFormData.specs,
              showLoginButton: messageFormData.showLoginButton,
              showDownArrow: messageFormData.showDownArrow,
              paramValue: messageFormData.paramValue,
              booking: messageFormData.booking,
            }
          : null,

      welcome:
        messageFormData.templateType === "welcome"
          ? messageFormData.welcome
          : null,
    };

    let response;
    if (isEditMode) {
      response = await updateOnboardTemplateAPI(
        payload,
        setIsSubmitting,
        token
      );
    } else {
      response = await createOrUpdateOnboardTemplateAPI(
        payload,
        setIsSubmitting,
        token
      );
    }

    if (response?.success) {
      toast.success(
        isEditMode
          ? "Template updated successfully"
          : "Template created successfully"
      );
      onSubmit(response.data);
      onClose();
      setMessageFormData(initialMessageFormState);
      setFetchMessage(true);
    } else {
      toast.error(response?.message || "Error creating/updating Template");
    }

    setIsSubmitting(false);
  };

  const handleCancel = () => {
    setMessageFormData(initialMessageFormState);
    onClose();
  };

  const isFormValid = useMemo(() => {
    if (isEditMode) return !!messageFormData.status;

    if (messageFormData.templateType === "signUp") {
      return (
        !!messageFormData.title &&
        !!messageFormData.specs?.length &&
        !!messageFormData.status
      );
    } else if (messageFormData.templateType === "welcome") {
      const welcome = messageFormData.welcome || {};
      return !!welcome.title && !!welcome.subTitle && !!messageFormData.status;
    }
    return false;
  }, [messageFormData, isEditMode]);

  if (isOpen && isLoadingLimits) {
    return (
      <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50 px-4">
        <div className="relative z-50 max-h-[90vh] w-full max-w-6xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl dark:bg-gray-900">
          <div className="flex flex-col items-center justify-center p-8">
            <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-4 border-blue-600"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-300">Loading</p>
          </div>
        </div>
      </div>
    );
  }

  const currentLimits = charLimits || { min: 10, max: 15 };

  if (!isOpen) return null;

  return (
    <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50 px-4">
      <div className="relative z-50 max-h-[90vh] w-full max-w-6xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl dark:bg-gray-900">
        <button
          type="button"
          onClick={handleCancel}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 focus:outline-none dark:hover:text-gray-100"
        >
          <IoMdClose size={24} />
        </button>

        <form onSubmit={handleSubmit} className="w-full">
          {!(isEditMode || isAddMode) && (
            <div className="mb-6 flex justify-center">
              <div className="w-full max-w-sm">
                <label className="mb-1 block text-center font-semibold text-gray-700 dark:text-white">
                  Template Type
                </label>
                <select
                  name="templateType"
                  value={messageFormData.templateType || "signUp"}
                  onChange={handleChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                >
                  <option value="signUp">Sign Up</option>
                  <option value="welcome">Welcome</option>
                </select>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {messageFormData.templateType === "signUp" && (
              <>
                <div>
                  <label className="mb-1 block font-semibold text-gray-700 dark:text-white">
                    Title
                  </label>
                  <input
                    disabled={isEditMode}
                    type="text"
                    name="title"
                    value={messageFormData.title || ""}
                    onChange={handleChange}
                    maxLength={currentLimits.max}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                  />
                  <CharCountDisplay
                    value={messageFormData.title}
                    fieldName="Title"
                  />
                </div>
                <div>
                  <label className="mb-1 block font-semibold text-gray-700 dark:text-white">
                    Subtitle
                  </label>
                  <input
                    type="text"
                    disabled={isEditMode}
                    name="subTitle"
                    value={messageFormData.subTitle || ""}
                    onChange={handleChange}
                    maxLength={currentLimits.max}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                  />
                  <CharCountDisplay
                    value={messageFormData.subTitle}
                    fieldName="Subtitle"
                  />
                </div>
              </>
            )}

            {messageFormData.templateType === "welcome" && (
              <>
                <div>
                  <label className="mb-1 block font-semibold text-gray-700 dark:text-white">
                    Welcome Title
                  </label>
                  <input
                    type="text"
                    disabled={isEditMode}
                    value={messageFormData.welcome?.title || ""}
                    onChange={(e) =>
                      handleWelcomeChange("title", e.target.value)
                    }
                    maxLength={currentLimits.max}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                  />
                  <CharCountDisplay
                    value={messageFormData.welcome?.title}
                    fieldName="Welcome Title"
                  />
                </div>
                <div>
                  <label className="mb-1 block font-semibold text-gray-700 dark:text-white">
                    Welcome Subtitle
                  </label>
                  <input
                    type="text"
                    disabled={isEditMode}
                    value={messageFormData.welcome?.subTitle || ""}
                    onChange={(e) =>
                      handleWelcomeChange("subTitle", e.target.value)
                    }
                    maxLength={currentLimits.max}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                  />
                  <CharCountDisplay
                    value={messageFormData.welcome?.subTitle}
                    fieldName="Welcome Subtitle"
                  />
                </div>
                <div>
                  <label className="mb-1 block font-semibold text-gray-700 dark:text-white">
                    Button Name
                  </label>
                  <input
                    disabled={isEditMode}
                    type="text"
                    value={messageFormData.welcome?.buttonName || ""}
                    onChange={(e) =>
                      handleWelcomeChange("buttonName", e.target.value)
                    }
                    maxLength={currentLimits.max}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                  />
                  <CharCountDisplay
                    value={messageFormData.welcome?.buttonName}
                    fieldName="Button Name"
                  />
                </div>
                <div>
                  <label className="mb-1 block font-semibold text-gray-700 dark:text-white">
                    Login Link
                  </label>
                  <input
                    disabled={isEditMode}
                    type="text"
                    value={messageFormData.welcome?.loginLink || ""}
                    onChange={(e) =>
                      handleWelcomeChange("loginLink", e.target.value)
                    }
                    maxLength={currentLimits.max}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                  />
                  <CharCountDisplay
                    value={messageFormData.welcome?.loginLink}
                    fieldName="Login Link"
                  />
                </div>
              </>
            )}

            <div className="flex gap-6 md:col-span-2">
              {messageFormData.templateType !== "welcome" && (
                <div className="flex-1">
                  <label className="mb-1 block font-semibold text-gray-700 dark:text-white">
                    Spec
                  </label>
                  {messageFormData.specs?.map((spec, index) => (
                    <div key={index} className="mb-2 flex items-center gap-2">
                      <select
                        value={spec.icon}
                        onChange={(e) =>
                          handleArrayChange(
                            "specs",
                            index,
                            "icon",
                            e.target.value
                          )
                        }
                        className="w-1/4 rounded-md border border-gray-300 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                      >
                        <option value={spec.icon}>{spec.icon}</option>
                      </select>
                      <input
                        type="text"
                        value={spec.label}
                        onChange={(e) =>
                          handleArrayChange(
                            "specs",
                            index,
                            "label",
                            e.target.value
                          )
                        }
                        disabled={isEditMode}
                        placeholder="Label"
                        maxLength={currentLimits.max}
                        className="w-3/4 rounded-md border border-gray-300 px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                      />
                      <CharCountDisplay
                        value={spec.label}
                        fieldName="Spec Label"
                      />
                      <button
                        type="button"
                        onClick={() => removeSpec(index)}
                        className="px-2 font-bold text-red-500"
                      >
                        X
                      </button>
                    </div>
                  ))}
                  <button
                    disabled={isEditMode}
                    type="button"
                    onClick={addSpec}
                    className="font-bold text-green-600"
                  >
                    + Add Spec
                  </button>
                </div>
              )}
              <div className="flex-1">
                <label className="mb-1 block font-semibold text-gray-700 dark:text-white">
                  Status
                </label>
                <div className="mt-1 flex items-center gap-4">
                  <label className="flex items-center gap-1 dark:text-white">
                    <input
                      type="radio"
                      name="status"
                      value="active"
                      checked={messageFormData.status === "active"}
                      onChange={handleChange}
                      className="cursor-pointer"
                    />{" "}
                    Active
                  </label>
                  <label className="flex items-center gap-1 dark:text-white">
                    <input
                      type="radio"
                      name="status"
                      value="inactive"
                      checked={messageFormData.status === "inactive"}
                      onChange={handleChange}
                      className="cursor-pointer"
                    />{" "}
                    Inactive
                  </label>
                </div>
              </div>
            </div>

            {messageFormData.templateType === "signUp" && (
              <div>
                <label className="mb-1 block font-semibold text-gray-700 dark:text-white">
                  Booking Type
                </label>
                <select
                  name="booking"
                  value={messageFormData.booking || "flight"}
                  disabled={isEditMode}
                  onChange={handleChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                >
                  <option value="flight">Flight</option>
                  <option value="stay">Stay</option>
                  <option value="flightAndStay">Flight + Stay</option>
                </select>
              </div>
            )}

            <div>
              <label className="mb-1 block font-semibold text-gray-700 dark:text-white">
                Show Login Button
              </label>
              <select
                disabled={isEditMode}
                name="showLoginButton"
                value={messageFormData.showLoginButton || false}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
              >
                <option value={true}>Yes</option>
                <option value={false}>No</option>
              </select>
            </div>
            {messageFormData.templateType !== "welcome" && (
              <div>
                <label className="mb-1 block font-semibold text-gray-700 dark:text-white">
                  Show Down Arrow
                </label>
                <select
                  disabled={isEditMode}
                  name="showDownArrow"
                  value={messageFormData.showDownArrow || false}
                  onChange={handleChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                >
                  <option value={true}>Yes</option>
                  <option value={false}>No</option>
                </select>
              </div>
            )}
            <div>
              <label className="mb-1 block font-semibold text-gray-700 dark:text-white">
                TID
              </label>
              <input
                disabled={isEditMode}
                type="text"
                name="paramValue"
                value={messageFormData.paramValue || ""}
                onChange={handleChange}
                maxLength={currentLimits.max}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
              />
              <CharCountDisplay
                value={messageFormData.paramValue}
                fieldName="TID"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-4">
            <button
              type="submit"
              className={`rounded-md bg-green-600 px-4 py-2 text-white hover:bg-green-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${
                isSubmitting || !isFormValid
                  ? "cursor-not-allowed opacity-50"
                  : ""
              }`}
              disabled={isSubmitting || !isFormValid}
            >
              {isEditMode ? "Update" : "Add"}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateOrUpdateOnboard;
